import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { useDreams } from "../contexts/DreamsContext";
import {
  SpinnerGap, Star, CalendarBlank, Trophy, ListChecks, Plus, Compass, Sparkle,
  ShareNetwork, Copy, ArrowsClockwise, X, Check,
} from "@phosphor-icons/react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}

export default function DreamsDashboard() {
  const { me } = useDreams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="flex items-center gap-2 text-[#978D78]">
      <SpinnerGap size={18} className="animate-spin" /> A carregar o teu próximo passo...
    </div>
  );
  const dream = data?.next_dream;
  const stats = data?.stats || {};
  const first = me?.user?.NomeCompleto?.split(" ")[0] || "Sonhador";

  return (
    <div className="space-y-10 pb-12">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#6F4F1D]/80">Olá, {first}</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-1">
            O teu <span className="text-[#6F4F1D]">próximo</span> sonho
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-3 py-1 rounded-full bg-white border border-[#E6E1D6] flex items-center gap-1.5">
            <Star size={12} weight="fill" className="text-[#6F4F1D]" /> {stats.pontos || 0} pontos
          </span>
          <span className="px-3 py-1 rounded-full bg-white border border-[#E6E1D6]">
            {stats.sonhos_abertos || 0} abertos · {stats.sonhos_concluidos || 0} concluídos
          </span>
          {dream && (
            <button
              onClick={() => setShareOpen(true)}
              data-testid="share-board-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#6F4F1D]/15 border border-[#6F4F1D]/30 text-[#6F4F1D] hover:bg-[#6F4F1D]/25 transition-colors"
            >
              <ShareNetwork size={12} weight="fill" /> Partilhar
            </button>
          )}
        </div>
      </header>
      {shareOpen && <ShareModal onClose={() => setShareOpen(false)} />}

      {dream ? (
        <section className="grid md:grid-cols-5 gap-6 rounded-3xl border border-[#E6E1D6] bg-gradient-to-br from-violet-900/30 via-fuchsia-900/10 to-amber-900/10 p-6 md:p-10">
          {/* Hero image */}
          <div className="md:col-span-2 relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-amber-500/30 to-fuchsia-700/30 flex items-center justify-center">
            {dream.FotoSonhoUrl ? (
              <img
                src={dream.FotoSonhoUrl}
                alt={dream.Titulo}
                className="w-full h-full object-cover"
              />
            ) : (
              <Sparkle size={64} weight="fill" className="text-[#6F4F1D]/40" />
            )}
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-[#6F4F1D] text-[#0F1829] text-[10px] font-bold uppercase tracking-wider">
              Próximo
            </span>
          </div>

          <div className="md:col-span-3 flex flex-col justify-center">
            <h2 className="text-3xl sm:text-4xl font-semibold leading-tight" data-testid="next-dream-title">{dream.Titulo}</h2>
            {dream.Descricao && (
              <p className="text-[#4B5563] mt-3 leading-relaxed text-sm">{dream.Descricao}</p>
            )}
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <CalendarBlank size={16} className="text-[#6F4F1D]" />
                <span className="text-[#4B5563]">Alvo:</span>
                <strong className="text-white">{fmtDate(dream.DataAlvo)}</strong>
              </div>
              {dream.Valor != null && (
                <div className="flex items-center gap-2">
                  <span className="text-[#6F4F1D]">€</span>
                  <strong className="text-white">{Number(dream.Valor).toLocaleString("pt-PT")}</strong>
                </div>
              )}
              {dream.Premio && (
                <div className="col-span-2 flex items-start gap-2">
                  <Trophy size={16} className="text-[#6F4F1D] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#6F4F1D]/80">Prémio</p>
                    <p className="text-sm text-white">{dream.Premio}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className="rounded-3xl border border-dashed border-[#E6E1D6] p-12 text-center">
          <Sparkle size={32} weight="fill" className="text-[#6F4F1D]/60 mx-auto" />
          <h2 className="text-2xl font-semibold mt-3">Ainda não tens sonhos definidos</h2>
          <p className="text-[#4B5563] mt-2 max-w-md mx-auto text-sm">
            Começa por descrever a tua vida de sonho — a IA sugere-te 8-10 sonhos concretos
            para começares a trabalhar.
          </p>
          <Link to="/dreams/vision" data-testid="empty-cta-vision"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] font-semibold text-sm">
            <Compass size={14} weight="fill" /> Definir vida de sonho
          </Link>
        </section>
      )}

      <section className="rounded-2xl border border-[#E6E1D6] p-6" data-testid="upcoming-tasks-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ListChecks size={18} className="text-[#6F4F1D]" /> Próximas 10 tarefas
          </h3>
          <Link to="/dreams/sonhos" className="text-xs text-[#978D78] hover:text-[#0F1829]">Ver sonhos →</Link>
        </div>
        {(data?.upcoming_tasks || []).length === 0 ? (
          <div className="text-sm text-[#978D78] italic" data-testid="upcoming-tasks-empty">
            Ainda não há tarefas pendentes. Abre um sonho e clica em
            <span className="not-italic font-medium text-[#0F1829]"> “Gerar plano com IA” </span>
            para criar o plano de acção.
          </div>
        ) : (
          <ul className="divide-y divide-[#F5F2EB]" data-testid="upcoming-tasks-list">
            {data.upcoming_tasks.map((t) => {
              const dueDate = t.Data ? new Date(t.Data) : null;
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const overdue = dueDate && dueDate < today;
              const dueTodayDay = dueDate && dueDate.getTime() === today.getTime();
              return (
                <li key={t.ID_Tarefa} className="py-2.5 flex items-start gap-3" data-testid={`upcoming-task-${t.ID_Tarefa}`}>
                  <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${overdue ? "bg-[#C2410C]" : dueTodayDay ? "bg-amber-500" : "bg-[#6F4F1D]"}`} />
                  <div className="flex-1 min-w-0">
                    <Link to={`/dreams/sonhos/${t.ID_Sonho}`} className="block text-sm font-medium text-[#0F1829] truncate hover:underline">
                      {t.Titulo}
                    </Link>
                    <div className="text-[11px] text-[#978D78] flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className="truncate" title={t.SonhoTitulo}>{t.SonhoTitulo}</span>
                      {dueDate && (
                        <>
                          <span>·</span>
                          <span className={overdue ? "text-[#C2410C] font-medium" : dueTodayDay ? "text-amber-700 font-medium" : ""}>
                            {overdue ? "Atrasada · " : dueTodayDay ? "Hoje · " : ""}
                            {fmtDate(t.Data)}
                          </span>
                        </>
                      )}
                      {t.HoraInicio && <><span>·</span><span>{String(t.HoraInicio).slice(0, 5)}</span></>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className="flex justify-end">
        <Link to="/dreams/sonhos" data-testid="cta-new-dream"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white hover:bg-white text-sm">
          <Plus size={14} /> Adicionar sonho
        </Link>
      </div>
    </div>
  );
}


function ShareModal({ onClose }) {
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/share/generate");
        if (!cancelled) setToken(data.token);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const shareUrl = token ? `${window.location.origin}/dreams/share/${token}` : "";

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* ignore */ }
  };

  const regenerate = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/share/regenerate");
      setToken(data.token);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!confirm("Revogar este link? Quem o tiver deixará de ver o teu Dream Board.")) return;
    setBusy(true);
    try {
      await api.delete("/share");
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="share-modal"
           className="bg-[#F5F2EB] border border-[#E6E1D6] rounded-2xl w-full max-w-lg">
        <header className="border-b border-[#E6E1D6] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShareNetwork size={18} weight="fill" className="text-[#6F4F1D]" />
            <h2 className="text-lg font-semibold">Partilhar Dream Board</h2>
          </div>
          <button onClick={onClose} className="text-[#978D78] hover:text-white" data-testid="share-close"><X size={20} /></button>
        </header>
        <div className="p-6 space-y-4">
          <p className="text-sm text-[#4B5563]">
            Quem aceder a este link vê o teu <strong>próximo sonho</strong> (título, descrição,
            data alvo, prémio e foto). Sem dados pessoais — só inspiração.
          </p>
          {busy && !token ? (
            <p className="text-sm text-[#978D78] flex items-center gap-2"><SpinnerGap size={14} className="animate-spin" /> A criar link...</p>
          ) : err ? (
            <p className="text-sm text-rose-300">{err}</p>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  data-testid="share-link-input"
                  readOnly
                  value={shareUrl}
                  className="flex-1 bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm text-white"
                  onFocus={(e) => e.target.select()}
                />
                <button onClick={copyLink} data-testid="share-copy"
                        className={`px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 transition-all ${
                          copied
                            ? "bg-emerald-400 text-[#0F1829]"
                            : "bg-[#6F4F1D] text-[#0F1829] hover:shadow-[0_0_20px_rgba(245,158,11,0.4)]"
                        }`}>
                  {copied ? <><Check size={14} weight="bold" /> Copiado</> : <><Copy size={14} weight="bold" /> Copiar</>}
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-xs">
                <a href={`https://wa.me/?text=${encodeURIComponent(`Quero partilhar contigo o meu Dream Board: ${shareUrl}`)}`}
                   target="_blank" rel="noopener noreferrer"
                   data-testid="share-whatsapp"
                   className="px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25">
                  WhatsApp
                </a>
                <a href={`mailto:?subject=${encodeURIComponent("O meu Dream Board")}&body=${encodeURIComponent(`Quero partilhar contigo o meu Dream Board:\n\n${shareUrl}`)}`}
                   data-testid="share-email"
                   className="px-3 py-1.5 rounded-full bg-white border border-[#E6E1D6] text-[#4B5563] hover:bg-white">
                  Email
                </a>
                <button onClick={regenerate} disabled={busy} data-testid="share-regen"
                        className="ml-auto inline-flex items-center gap-1.5 text-[#978D78] hover:text-white">
                  <ArrowsClockwise size={12} /> Novo link
                </button>
                <button onClick={revoke} disabled={busy} data-testid="share-revoke"
                        className="text-rose-300 hover:text-rose-200">
                  Revogar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
